// components/carousel/TopicFeedCarousels.js
//
// The mobile topic view, shared by every country home page.
//
// When topics moved from SectionPage onto the home pages, each page grew its own
// copy of the same carousel. They also inherited the same three faults:
//
//   1. Filtering the topic view by showOnHomePage. That flag decides what
//      appears in the home OVERVIEW (Glance, All Categories); the topic view is
//      the old section page and shows every feed in the branch. SectionPage
//      never filtered here — the Mexico and UK transformers in feedsTransformer
//      even carry a comment saying so.
//   2. Sending department feeds to /feed/{schema}/{table} instead of the
//      department's own page. feedsTransformer keeps that link on every
//      flattened carousel item as `groupLink`; the Canada/Mexico/UK executive
//      transformers use `agencyRoute` for the same thing.
//   3. Rendering one carousel per feed inside a department, so a department's
//      feeds each got their own heading and all pointed at the same page.
//      MultiDepartmentSection grouped them under the department name.
//
// Callers pass feeds already transformed by getMobileFeedsForSection.

import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { ChevronRight } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import useFeedData from '../../hooks/useFeedData';
import useCommitteeFeedData from '../../hooks/useCommitteeFeedData';
import { isCommitteeFeed, generateCommitteeFeedUrl } from '../../utils/committeeFeedHelpers';
import FeedItem from '../feed/FeedItem';

// ═══════════════════════════════════════════════════════════════════
// DESTINATIONS
// ═══════════════════════════════════════════════════════════════════

// A feed that belongs to a department (White House, House, Senate, a cabinet
// department, a Canadian agency, a Mexican secretaría, a UK ministry) has a page
// of its own. Headings prefer it over the generated feed URL.
export const departmentLinkFor = (feed) => (feed && (feed.groupLink || feed.agencyRoute)) || null;

// /feed/{schema}/{table} for a feed's own full-feed view. fallbackSchema covers
// the country configs whose endpoints omit schema (Mexico, UK).
export const feedUrlFor = (apiParams, fallbackSchema = '') => {
  let clean = apiParams || '';
  if (clean.startsWith('&') || clean.startsWith('?')) clean = clean.substring(1);
  const p = new URLSearchParams(clean);
  const schema = p.get('schema') || fallbackSchema;
  const table = p.get('table');
  return schema && table ? `/feed/${schema}/${table}` : null;
};

// Where a single feed's heading should go. Committee materials have no
// schema/table, so they need the committee URL builder instead.
export const destinationFor = (feed, fallbackSchema = '') => {
  if (!feed) return null;
  const endpoint = feed.apiParams || feed.apiEndpoint;
  if (isCommitteeFeed(feed)) return generateCommitteeFeedUrl(endpoint);
  return feedUrlFor(endpoint, fallbackSchema);
};

// ═══════════════════════════════════════════════════════════════════
// SHELL
// ═══════════════════════════════════════════════════════════════════

// Heading, horizontal scroller, View all card. Callers build the cards, which is
// what lets the standard, committee and department carousels each use their own
// data hook without duplicating the markup. Class names match the existing
// carousel CSS on every home page.
const ScrollCarousel = ({ title, onOpen, viewAllLabel, cards }) => (
  <div className="homepage-branch-carousel">
    <div className="branch-header">
      <div className="branch-title" onClick={onOpen}>
        <span>{title}</span>
        <ChevronRight className="branch-chevron" />
      </div>
    </div>
    <div className="carouselGroupContainer news-section">
      <div className="scroll-container">
        <div className="gradient-left" /><div className="gradient-right" />
        <div className="scroll-area">
          <div className="card-container">
            {cards}
            <div className="card-wrapper view-all-card">
              <div id="viewFullPageButton">
                <Button id="viewFullLegislativePageButtonMobile" size="lg" onClick={onOpen}>{viewAllLabel}</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const LoadingCard = () => (
  <div className="card-wrapper"><div className="mobile-carousel-loading"><Spinner animation="grow" variant="light" /></div></div>
);

const EmptyCard = ({ error }) => (
  <div className="card-wrapper"><div className="mobile-carousel-empty"><p>{error ? 'Error loading' : 'No content'}</p></div></div>
);

// ═══════════════════════════════════════════════════════════════════
// CARDS (one item, used inside a department carousel)
// ═══════════════════════════════════════════════════════════════════

const StandardCard = ({ feed, fallbackSchema, isRecentsItem = false }) => {
  const { data, loading, error } = useFeedData(feed.apiParams, 1);
  if (loading) return <div className="mobile-carousel-loading"><Spinner animation="grow" variant="light" /></div>;
  if (error || !data || data.length === 0) {
    return <div className="mobile-carousel-empty"><p>{error ? 'Error loading content' : 'No content available'}</p></div>;
  }
  const urlParams = new URLSearchParams((feed.apiParams || '').replace(/^[&?]/, ''));
  const schema = urlParams.get('schema') || fallbackSchema;
  const table = urlParams.get('table');
  const displayFeedName = isRecentsItem && data[0] ? (data[0].collection_name || data[0].source || feed.name) : feed.name;
  const displayTable = isRecentsItem && data[0]?.source ? data[0].source : table;
  return (
    <div style={{ height: isRecentsItem ? 'auto' : '160px', minHeight: '160px', overflow: 'hidden' }}>
      <FeedItem item={data[0]} feedName={displayFeedName} branch={feed.feedBranch} topic={feed.feedTopic} buttonName={feed.buttonName} schema={schema} table={displayTable} expanded={false} isRecentsItem={isRecentsItem} variant="homepage" />
    </div>
  );
};

// Committee materials come from /committees, not /posts/recent, so they need
// their own hook and a fixed schema/table for routing.
const CommitteeCard = ({ feed }) => {
  const { data, loading, error } = useCommitteeFeedData(feed.apiParams, 1);
  const items = Array.isArray(data) ? data : [];
  if (loading) return <div className="mobile-carousel-loading"><Spinner animation="grow" variant="light" /></div>;
  if (error || items.length === 0) {
    return <div className="mobile-carousel-empty"><p>{error ? 'Error loading content' : 'No content available'}</p></div>;
  }
  return (
    <div style={{ height: '160px', minHeight: '160px', overflow: 'hidden' }}>
      <FeedItem item={items[0]} feedName={feed.name} branch={feed.feedBranch} topic={feed.feedTopic} buttonName={feed.buttonName} schema="united_states_of_america" table="committee_materials" expanded={false} variant="homepage" />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CAROUSELS
// ═══════════════════════════════════════════════════════════════════

// One standalone feed, several of its items across the scroller.
const MultiFeedCarousel = ({ feed, itemCount = 5, fallbackSchema = '', isRecentsItem = false }) => {
  const navigate = useNavigate();
  const { data, loading, error } = useFeedData(feed.apiParams, itemCount);
  const urlParams = new URLSearchParams((feed.apiParams || '').replace(/^[&?]/, ''));
  const schema = urlParams.get('schema') || fallbackSchema;
  const table = urlParams.get('table') || '';

  const handleViewAll = () => {
    const target = departmentLinkFor(feed) || destinationFor(feed, fallbackSchema);
    if (!target) return;
    window.scrollTo(0, 0);
    navigate(target);
  };

  let cards;
  if (loading) {
    cards = <LoadingCard />;
  } else if (error || !data || data.length === 0) {
    cards = <EmptyCard error={error} />;
  } else {
    cards = data.map((item, idx) => {
      const displayFeedName = isRecentsItem ? (item.collection_name || item.source || feed.name) : feed.name;
      const displayTable = isRecentsItem && item.source ? item.source : table;
      return (
        <div key={item.id || idx} className="card-wrapper">
          <div className="card-content">
            <div className="courtNewsMobile">
              <div style={{ height: '160px', minHeight: '160px', overflow: 'hidden' }}>
                <FeedItem item={item} feedName={displayFeedName} branch={feed.feedBranch} topic={feed.feedTopic} buttonName={feed.buttonName} schema={schema} table={displayTable} expanded={false} isRecentsItem={isRecentsItem} variant="homepage" />
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  return (
    <ScrollCarousel
      title={feed.name}
      onOpen={handleViewAll}
      viewAllLabel={`View all ${feed.name}`}
      cards={cards}
    />
  );
};

// Same shape on the committee API. Kept separate because the hook can't be
// chosen conditionally inside one component.
const CommitteeFeedCarousel = ({ feed, itemCount = 5 }) => {
  const navigate = useNavigate();
  const { data, loading, error } = useCommitteeFeedData(feed.apiParams, itemCount);
  const items = Array.isArray(data) ? data : [];

  const handleViewAll = () => {
    window.scrollTo(0, 0);
    navigate(generateCommitteeFeedUrl(feed.apiParams));
  };

  let cards;
  if (loading) {
    cards = <LoadingCard />;
  } else if (error || items.length === 0) {
    cards = <EmptyCard error={error} />;
  } else {
    cards = items.map((item, idx) => (
      <div key={item.id || idx} className="card-wrapper">
        <div className="card-content">
          <div className="courtNewsMobile">
            <div style={{ height: '160px', minHeight: '160px', overflow: 'hidden' }}>
              <FeedItem item={item} feedName={feed.name} branch={feed.feedBranch} topic={feed.feedTopic} buttonName={feed.buttonName} schema="united_states_of_america" table="committee_materials" expanded={false} variant="homepage" />
            </div>
          </div>
        </div>
      </div>
    ));
  }

  return (
    <ScrollCarousel
      title={feed.name}
      onOpen={handleViewAll}
      viewAllLabel={`View all ${feed.name}`}
      cards={cards}
    />
  );
};

// One carousel per department: the department name is the heading and each of
// its feeds gets a card with its own sub-heading.
const DepartmentFeedCarousel = ({ group, items, fallbackSchema = '' }) => {
  const navigate = useNavigate();
  const title = group.groupDisplayName || group.name;
  const departmentLink = departmentLinkFor(group);

  const openDepartment = () => {
    if (!departmentLink) return;
    window.scrollTo(0, 0);
    navigate(departmentLink);
  };

  const openFeed = (feed) => {
    const target = destinationFor(feed, fallbackSchema) || departmentLink;
    if (!target) return;
    window.scrollTo(0, 0);
    navigate(target);
  };

  const cards = items.map((item) => (
    <div key={item.id} className="card-wrapper">
      <div className="mobile-feed-heading-row">
        <h5 className="feed-heading-mobile" onClick={() => openFeed(item)}>
          <div className="title-container"><span className="heading-text2">{item.name}</span><ChevronRight className="heading-chevron" /></div>
        </h5>
      </div>
      <div className="card-content">
        <div className="courtNewsMobile">
          {isCommitteeFeed(item)
            ? <CommitteeCard feed={item} />
            : <StandardCard feed={item} fallbackSchema={fallbackSchema} />}
        </div>
      </div>
    </div>
  ));

  return (
    <ScrollCarousel
      title={title}
      onOpen={openDepartment}
      viewAllLabel={group.groupButtonText || `View all ${title} news`}
      cards={cards}
    />
  );
};

// ═══════════════════════════════════════════════════════════════════
// ENTRY POINT
// ═══════════════════════════════════════════════════════════════════

/**
 * The mobile topic view for one branch.
 *
 * @param {Array}  feeds          Feeds from getMobileFeedsForSection, unfiltered.
 * @param {number} itemCount      Items per standalone feed carousel.
 * @param {string} fallbackSchema Schema for configs whose endpoints omit one.
 * @param {boolean} isRecentsItem Recents cards take their name from the item.
 */
const TopicFeedCarousels = ({ feeds = [], itemCount = 5, fallbackSchema = '', isRecentsItem = false }) => {
  // Member placeholders (Featured Representative, Featured Senator) carry no
  // endpoint. Dropped rather than rendered until MemberCarouselCard is wired in.
  const usable = feeds.filter((f) => f && f.apiParams);
  if (usable.length === 0) return null;

  const rendered = [];
  const seenGroups = new Set();

  usable.forEach((feed) => {
    if (feed.group) {
      if (seenGroups.has(feed.group)) return;
      seenGroups.add(feed.group);
      rendered.push(
        <DepartmentFeedCarousel
          key={feed.group}
          group={feed}
          items={usable.filter((f) => f.group === feed.group)}
          fallbackSchema={fallbackSchema}
        />
      );
      return;
    }
    rendered.push(
      isCommitteeFeed(feed)
        ? <CommitteeFeedCarousel key={feed.id} feed={feed} itemCount={itemCount} />
        : <MultiFeedCarousel key={feed.id} feed={feed} itemCount={itemCount} fallbackSchema={fallbackSchema} isRecentsItem={isRecentsItem} />
    );
  });

  return <>{rendered}</>;
};

export default TopicFeedCarousels;